<template>
  <b-form>
    <BaseModal name="subscriptions-advanced-filters" size="lg" :hide-footer="true" :title="$t('common.text_0001')">
      <div style="display: flex; flex-direction: column; align-items: flex-start; justify-content: left; gap: 8px;">
        
        <!-- Produtos -->
          <GenericFilter
            :label="$t('common.text_0007')"
            :options="products"
            v-model="selectedProducts"
            :loading="loadingProducts"
            :limit="5"
            placeholder="Selecione seus produtos"
            @search-change="searchProducts"
            :show-select-all="true"
            @close="onProductsClose"
            :searchable="true"
          />

        <!-- Ofertas -->
        <GenericFilter
          :label="$t('common.text_0008')"
          :options="offers"
          v-model="selectedOffers"
          :loading="loadingOffers"
          :limit="5"
          placeholder="Pesquise a oferta atrelada ao produto"
          :group-label="'productName'"
          :group-values="'offerOptions'"
          :group-select="true"
          :disabled="!selectedProducts.length"
          :show-select-all="true"
          :searchable="true"
        />
        
        <!-- Contratos -->
        <GenericFilter
          :label="$t('common.text_0010')"
          v-model="contractsInput"
          placeholder="Pesquise o ID do contrato"
          :text-mode="true"
          :searchable="true"
        />

        <!-- Status -->
        <GenericFilter
          :label="$t('common.text_0009')"
          :options="statusOptions"
          v-model="selectedStatus"
          :limit="5"
          placeholder="Selecione o status"
          :show-select-all="true"
          :searchable="true"
        />

        <div class="w-100" style="margin-bottom: -12px;">
          <label class="label-compact">Por cliente</label>
          
          <b-form-radio-group v-model="selectedForClientOption" style="new-style" class="mb-2" name="radio-options">
            <div>
              <b-form-radio value="email">E-mail</b-form-radio>
            </div>
            <div>
              <b-form-radio value="cpf_cnpj">CPF/CNPJ</b-form-radio>
            </div>
          </b-form-radio-group>

          <b-col sm="6" class="p-0">
            <BaseInput 
              id="email" 
              name="email" 
              type="text"
              :class="`mb-4 ${selectedForClientOption === 'email' ? 'd-block' : 'd-none'}`"
              v-model="email"
              v-validate="'email'"
              placeholder="Insira um e-mail"
              :isValid="!errors.has('email')"
              errorMessage="Insira um e-mail válido"
            />

            <BaseInput 
              v-if="selectedForClientOption === 'cpf_cnpj'"
              id="cpf_cnpj" 
              name="cpf_cnpj"
              type="text"
              :class="`mb-4 ${selectedForClientOption === 'cpf_cnpj' ? 'd-block' : 'd-none'}`"
              v-model="cpfCnpj"
              v-validate="'cpf_cnpj'"
              placeholder="Insira um CPF ou CNPJ"
              v-mask="['###.###.###-##', '##.###.###/####-##']"
              :isValid="!errors.has('cpf_cnpj')"
              errorMessage="Insira um CPF ou CNPJ válido"
            />
          </b-col>
        </div>

        <GenericFilter
          label="Afiliados"
          :options="affiliateOptions"
          v-model="selectedAffiliate"
          :limit="5"
          placeholder="Pesquise o afiliado"
          :show-select-all="true"
        />

        <GenericFilter
          label="Método de pagamento"
          :options="paymentMethodOptions"
          v-model="selectedPaymentMethod"
          :limit="5"
          placeholder="Selecione o método de pagamento"
          :show-select-all="true"
        />

        <div class="w-100">
          <label class="label-compact">Tipo do Produto</label>
          <b-form-checkbox-group id="format" v-model="formats" style="new-style" name="checkbox-options">
            <div>
              <b-form-checkbox value="POS_GRADUATION">Pós Graduação</b-form-checkbox>
              <b-form-checkbox value="EXTENSION_COURSE">Extensão</b-form-checkbox>
            </div>
          </b-form-checkbox-group>
        </div>

        <div class="w-100">
          <label class="label-compact">Demais filtros</label>
            <b-form-checkbox id="isSmartSale" v-model="isSmartSale" style="new-style" value="true">Venda Inteligente</b-form-checkbox>
        </div>
      </div>

      <div class="footer-buttons mt-3" >
        <BaseNewButton
          id="cancel-advanced-filters"
          variant="secondary"
          @click.prevent="$bvModal.hide('subscriptions-advanced-filters')"
        >
          {{ $t("common.text_0002") }}
        </BaseNewButton>
        <BaseNewButton
          id="apply-advanced-filters"
          variant="primary"
          ref="button"
          @click.prevent="onSubmit"
        >
          {{ $t("common.text_0005") }}
        </BaseNewButton>
      </div>
    </BaseModal>
  </b-form>
</template>

<script>
import ProductService from '@/services/resources/ProductService.js';
import OfferService from '@/services/resources/OfferService.js';
import ContractService from "@/services/resources/ContractService";
import Multiselect from 'vue-multiselect';
import { debounce } from 'lodash';
import GenericFilter from '@/components/GenericFilter.vue';
import BaseInput from '@/components/BaseInput.vue';

const productService = ProductService.build();
const offerService = OfferService.build();
const serviceContract = ContractService.build();

export default {
  name: 'AdvancedFiltersModal',
  components: { Multiselect, GenericFilter, BaseInput },
  data() {
    return {
      products: [],
      selectedProducts: [],
      loadingProducts: false,
      email: '',
      cpfCnpj: '',
      offers: [],
      selectedOffers: [],
      loadingOffers: false,
      loadingAffiliates: false,
      selectedForClientOption: 'email',
      affiliateOptions: [],
      selectedAffiliate: [],
      // Status
      statusOptions: [
        { id: 'trialing', name: this.$t('shared.status.trialing') },
        { id: 'paid', name: this.$t('shared.status.active') },
        { id: 'unpaid', name: this.$t('shared.status.unpaidSubscription') },
        { id: 'pending_payment', name: this.$t('shared.status.pendingSubscription') },
        { id: 'canceled', name: this.$t('shared.status.canceled') },
        { id: 'ended', name: this.$t('shared.status.ended') },
      ],
      selectedStatus: [],
      contractsInput: '',
      contractsInputError: '',
      offersToSelect: [],
      affiliateOptions: [],
      paymentMethodOptions: [
        { id: 'credit_card', name: this.$t('common.payment_method.credit_card') },
        { id: 'boleto', name: this.$t('common.payment_method.boleto') },
        { id: 'pix', name: this.$t('common.payment_method.pix') },
      ],
      selectedPaymentMethod: [],
      isSmartSale: null,
      formats: [],
    };
  },
  mounted() {
    this.fetchProducts();
    this.fetchAffiliateOptions();
  },
  methods: {
    resetFilters() {
      // Reseta todos os filtros para o estado inicial
      this.selectedProducts = [];
      this.selectedOffers = [];
      this.offers = [];
      this.selectedStatus = [];
      this.contractsInput = '';
      this.contractsInputError = '';
      this.offersToSelect = [];
      this.selectedForClientOption = 'email';
      this.email = '';
      this.cpfCnpj = '';
      this.selectedAffiliate = [];
      this.selectedPaymentMethod = [];
      this.isSmartSale = null;
      this.formats = [];
      // Força a atualização do componente
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    async fetchProducts(search = null) {
      try {
        this.loadingProducts = true;
        this.products = [];
        const data = {
          list: true,
          with_participants: true,
        };
        if (search) {
          data.search = search;
        }
        const products = await productService.search(data);
        this.products = products.map(({ id, name }) => ({ id, name }));
      } catch (error) {
        this.products = [];
        // eslint-disable-next-line no-console
        console.error('AdvancedFiltersModal::fetchProducts', error);
      } finally {
        this.loadingProducts = false;
      }
    },
    fetchAffiliateOptions(search = null) {
      this.loadingAffiliates = true;

      const payload = {
        id: '/affiliate'
      };

      serviceContract.read(payload)
        .then((data) => {
          if (!data || data.length === 0) {
            this.affiliateOptions = [];
            return;
          }

          this.affiliateOptions = data.map(({ id, name }) => ({ id, name }));
          console.log('fetchAffiliateOptions', this.affiliateOptions);
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('AdvancedFiltersModal::fetchAffiliateOptions', error);
        })
        .finally(() => {
          this.loadingAffiliates = false;
        });
    },
    async fetchOffersForProducts(products) {
      this.loadingOffers = true;
      this.offers = [];
      try {
        const productIds = products.map(product => product.id);
        const response = await offerService.search({ url: '/products', products: productIds });
        this.offers = response;
        // Junta todas as ofertas disponíveis de todos os produtos selecionados
        let allOffers = [];
        if (Array.isArray(this.offers)) {
          for (const group of this.offers) {
            if (group.offerOptions) {
              allOffers = allOffers.concat(group.offerOptions);
            }
          }
        }
        const availableOfferIds = allOffers.map(o => o.id);
        // Atualiza selectedOffers para garantir que são as mesmas instâncias de allOffers
        let selectedIds = (this.selectedOffers || []).map(o => o.id);
        let newSelectedOffers = allOffers.filter(o => selectedIds.includes(o.id));
        // Se houver offersToSelect (vindo do setFilters), adiciona essas à seleção, sem duplicar
        if (this.offersToSelect && this.offersToSelect.length > 0) {
          const toSelect = allOffers.filter(o => this.offersToSelect.includes(o.id));
          toSelect.forEach(o => {
            if (!newSelectedOffers.find(s => s.id === o.id)) {
              newSelectedOffers.push(o);
            }
          });
          this.offersToSelect = [];
        }
        this.selectedOffers = newSelectedOffers;
      } catch (error) {
        this.offers = [];
        // eslint-disable-next-line no-console
        console.error('AdvancedFiltersModal::fetchOffersForProducts', error);
      } finally {
        this.loadingOffers = false;
      }
    },
    searchProducts: debounce(function (search) {
      this.fetchProducts(search);
    }, 500),
    onProductsClose() {
      if (this.selectedProducts && this.selectedProducts.length > 0) {
        this.fetchOffersForProducts(this.selectedProducts);
      } else {
        this.offers = [];
        this.selectedOffers = [];
      }
    },
    async onSubmit() {
      const { selectedForClientOption } = this;

      // Validação condicional
      const validField = selectedForClientOption === 'email' ? 'email' : 'cpf_cnpj';
      if (!await this.$validator.validate(validField)) return;

      // Validação dos contratos
      this.contractsInputError = '';
      const contracts = this.contractsInput
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);

      if (contracts.some(id => !/^\d+$/.test(id))) {
        this.contractsInputError = 'Insira apenas números separados por vírgula.';
        return;
      }

      // Fechar modal
      this.$bvModal.hide('subscriptions-advanced-filters');
      console.log(this.formats);
      // Montar payload
      const payload = {
        ...(this.selectedProducts.length && {
          products: this.selectedProducts.map(p => p.id),
        }),
        ...(this.selectedOffers.length && {
          offers: this.selectedOffers.map(o => o.id),
        }),
        ...(this.selectedStatus.length && {
          status: this.selectedStatus.map(s => s.id),
        }),
        ...(contracts.length && { contracts }),
        ...(selectedForClientOption === 'email' && {
          clientEmail: this.email || '',
          clientDocument: '',
        }),
        ...(selectedForClientOption === 'cpf_cnpj' && {
          clientDocument: this.cpfCnpj || '',
          clientEmail: '',
        }),
        ...(this.selectedAffiliate.length && {
          affiliates: this.selectedAffiliate.map(({ id }) => id),
        }),
        ...(this.selectedPaymentMethod.length && {
          methods: this.selectedPaymentMethod.map(({ id }) => id),
        }),
        ...(this.formats.length && { formats: this.formats }),
        isSmartSale: this.isSmartSale,
      };

      this.$emit('apply-advanced-filters', payload);
    },
    isOptionSelected(type, option, force, obj) {
      let object = obj || this[type];
      return object?.value?.find(item => item.id === option) || force
        ? require('@/assets/img/icons/checkbox-area-selected.svg')
        : require('@/assets/img/icons/checkbox-area.svg');
    },
    setStatus(statusIds) {
      // Atualiza o status selecionado a partir de uma lista de ids
      if (!Array.isArray(statusIds)) {
        statusIds = [];
      }
      
      // Mapeia os IDs para os objetos completos do status
      const newSelectedStatus = this.statusOptions.filter(opt => statusIds.includes(opt.id));
      
      // Atualiza apenas se houver mudança
      if (JSON.stringify(this.selectedStatus) !== JSON.stringify(newSelectedStatus)) {
        this.selectedStatus = newSelectedStatus;
        
        // Força atualização do componente
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },
    setFilters({ products = [], offers = [], status = [], contracts = [], clientEmail = '', clientDocument = '', affiliates = [], methods = []}) {
      // Sincroniza todos os filtros do modal avançado
      // Produtos
      if (Array.isArray(products) && products.length > 0) {
        this.selectedProducts = this.products.filter(p => products.includes(p.id));
        this.offersToSelect = Array.isArray(offers) ? offers : [];
        this.$nextTick(() => {
          this.fetchOffersForProducts(this.selectedProducts);
        });
      } else {
        this.selectedProducts = [];
        this.offers = [];
        this.selectedOffers = [];
      }
      
      // Status - Garante que os objetos completos são usados
      if (Array.isArray(status) && status.length > 0) {
        this.selectedStatus = this.statusOptions.filter(opt => status.includes(opt.id));
      } else {
        this.selectedStatus = [];
      }
      
      // Contratos
      this.contractsInput = Array.isArray(contracts) ? contracts.join(',') : '';

      this.email = clientEmail;
      this.cpfCnpj = clientDocument;
      this.selectedForClientOption = clientEmail ? 'email' : 'cpf_cnpj';
      this.selectedAffiliate = this.affiliateOptions.filter(opt => affiliates.includes(opt.id));
      this.selectedPaymentMethod = this.paymentMethodOptions.filter(opt => methods.includes(opt.id));
      this.formats = this.formats;

      // Força atualização do componente
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
  },
  watch: {
    selectedProducts: {
      handler(newProducts) {
        if (Array.isArray(newProducts) && newProducts.length > 0) {
          this.fetchOffersForProducts(newProducts);
        } else {
          this.offers = [];
          this.selectedOffers = [];
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 20px;
  width: 100%;
}

.label-compact {
  margin-bottom: 2px;
  margin-top: 8px;
  font-weight: 500;
  font-size: 15px;
}

::v-deep(.modal-footer) {
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.offer-group-label-gray {
  color: #888;
  font-size: 13px;
  display: block;
  margin-bottom: 2px;
}

.select-all-link {
  color: var(--Default-gray-500, #6B7280);

  /* text-xs/leading-4/font-normal */
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}
::v-deep(.custom-radio) {
  padding: 0 !important;
  background: transparent !important;
}
</style>